// src/context/DataContext.js
/**
 * Shared Data Context for TimeMate Admin
 *
 * Provides centralized access to commonly-needed data (users, companies)
 * that would otherwise be fetched redundantly by multiple pages.
 *
 * OPTIMIZATION: Fetches data once on login, shares across all pages.
 * Eliminates 2-3 Firestore reads per page navigation.
 */
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useFeatures } from './FeatureContext';
import cacheService, { CACHE_TTL } from '../services/cacheService';

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const { user } = useAuth();
  const { companies, loading: featuresLoading } = useFeatures();

  // Users state - stores ALL users including admins (raw Firestore data)
  const [allUsersData, setAllUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  /**
   * Memoized filtered users - excludes Admin role
   * Admins only use the webapp, not the mobile app, so they shouldn't
   * appear in employee lists, time tracking, absences, reports, etc.
   */
  const users = useMemo(() => {
    return allUsersData.filter(u => u.role !== 'Admin');
  }, [allUsersData]);

  /**
   * Memoized users map for O(1) lookups - excludes Admin role
   */
  const usersMap = useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.id] = u; });
    return map;
  }, [users]);

  // Global holidays state (keyed by companyId)
  const [globalHolidays, setGlobalHolidays] = useState({});
  const [holidaysLoading, setHolidaysLoading] = useState(true);

  // Track last fetch time for smart refresh
  const [lastFetchTime, setLastFetchTime] = useState(null);

  /**
   * Fetch all users for admin's companies
   * Uses caching to reduce Firestore reads
   */
  const fetchUsers = useCallback(async (forceRefresh = false) => {
    if (!user?.adminCompanyIds || user.adminCompanyIds.length === 0) {
      setAllUsersData([]);
      setUsersLoading(false);
      return;
    }

    const adminCompanyIds = user.adminCompanyIds;
    // Sort IDs before joining to ensure consistent cache keys regardless of array order.
    // e.g., ["compB", "compA"] and ["compA", "compB"] both produce "compA,compB"
    const companyKey = adminCompanyIds.sort().join(',');
    const cacheKey = cacheService.generateKey('shared_users', { companies: companyKey });

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedUsers = await cacheService.get(cacheKey);
      if (cachedUsers) {
        // Cache stores all users; filtering happens via memoized `users`
        setAllUsersData(cachedUsers);
        setUsersLoading(false);
        setLastFetchTime(Date.now());
        return;
      }
    }

    try {
      setUsersLoading(true);

      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', 'in', adminCompanyIds)
      );
      const usersSnapshot = await getDocs(usersQuery);

      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Cache ALL users (including admins) - filtering happens at runtime
      await cacheService.set(cacheKey, usersData, CACHE_TTL.USER_PROFILES);

      // Set raw data; memoized `users` and `usersMap` will filter out admins
      setAllUsersData(usersData);

      setLastFetchTime(Date.now());
    } catch (error) {
      // Error handled silently
    } finally {
      setUsersLoading(false);
    }
  }, [user?.adminCompanyIds]);

  /**
   * Fetch global holidays for all admin's companies
   * Structure: global_holidays/{companyId}/holidays/{holidayId}
   */
  const fetchGlobalHolidays = useCallback(async (forceRefresh = false) => {
    if (!user?.adminCompanyIds || user.adminCompanyIds.length === 0) {
      setGlobalHolidays({});
      setHolidaysLoading(false);
      return;
    }

    const adminCompanyIds = user.adminCompanyIds;
    const companyKey = adminCompanyIds.sort().join(',');
    const cacheKey = cacheService.generateKey('global_holidays', { companies: companyKey });

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedHolidays = await cacheService.get(cacheKey);
      if (cachedHolidays) {
        setGlobalHolidays(cachedHolidays);
        setHolidaysLoading(false);
        return;
      }
    }

    try {
      setHolidaysLoading(true);

      const holidaysData = {};

      // Fetch holidays for each company
      for (const companyId of adminCompanyIds) {
        try {
          const holidaysQuery = query(
            collection(db, 'global_holidays', companyId, 'holidays')
          );
          const holidaysSnapshot = await getDocs(holidaysQuery);

          holidaysData[companyId] = holidaysSnapshot.docs.map(doc => {
            const data = doc.data();
            // Convert Firestore Timestamps to ISO strings if needed
            return {
              id: doc.id,
              ...data,
              date: data.date?.toDate ? data.date.toDate().toISOString() : data.date,
              end_date: data.end_date?.toDate ? data.end_date.toDate().toISOString() : data.end_date
            };
          });
        } catch (error) {
          // If subcollection doesn't exist for this company, use empty array
          holidaysData[companyId] = [];
        }
      }

      // Cache holidays (1 hour - holidays rarely change)
      await cacheService.set(cacheKey, holidaysData, CACHE_TTL.USER_PROFILES);

      setGlobalHolidays(holidaysData);
    } catch (error) {
      // Error handled silently - holidays will be empty
      setGlobalHolidays({});
    } finally {
      setHolidaysLoading(false);
    }
  }, [user?.adminCompanyIds]);

  /**
   * Force refresh all data
   * Call this after write operations that affect shared data
   */
  const refreshData = useCallback(async () => {
    await Promise.all([fetchUsers(true), fetchGlobalHolidays(true)]);
  }, [fetchUsers, fetchGlobalHolidays]);

  /**
   * Invalidate user cache
   * Call this after user-related write operations
   */
  const invalidateUsers = useCallback(async () => {
    if (!user?.adminCompanyIds) return;

    const companyKey = user.adminCompanyIds.sort().join(',');
    await cacheService.invalidatePattern('shared_users');
    await cacheService.invalidatePattern('dashboard_users');
  }, [user?.adminCompanyIds]);

  /**
   * Optimistically update a single user in the shared state
   * Call this after updating a user to immediately reflect changes across all pages
   * @param {string} userId - The ID of the user to update
   * @param {object} updates - The fields to update
   */
  const updateUserOptimistic = useCallback((userId, updates) => {
    // Update raw data; memoized `users` and `usersMap` will recompute automatically
    setAllUsersData(prevUsers =>
      prevUsers.map(u => u.id === userId ? { ...u, ...updates } : u)
    );
  }, []);

  /**
   * Get user by ID from cached map (O(1) lookup)
   */
  const getUserById = useCallback((userId) => {
    return usersMap[userId] || null;
  }, [usersMap]);

  /**
   * Get users filtered by company
   */
  const getUsersByCompany = useCallback((companyId) => {
    if (!companyId || companyId === 'all') {
      return users;
    }
    return users.filter(u => u.companyId === companyId);
  }, [users]);

  /**
   * Get active (online/pause) users
   */
  const getActiveUsers = useCallback((companyId = null) => {
    const filteredUsers = companyId && companyId !== 'all'
      ? users.filter(u => u.companyId === companyId)
      : users;

    return filteredUsers.filter(u =>
      u.status === 'online' || u.status === 'pause'
    );
  }, [users]);

  /**
   * Build a Set of holiday dates for a specific company
   * Handles multi-day holidays via end_date
   * Returns a Set of date strings in 'YYYY-MM-DD' format
   */
  const buildHolidayDateSet = useCallback((companyId) => {
    const holidays = globalHolidays[companyId] || [];
    const holidayDates = new Set();

    for (const holiday of holidays) {
      if (!holiday.date) continue;

      try {
        // Normalize start date
        const startDate = new Date(holiday.date);
        if (isNaN(startDate.getTime())) continue; // Skip invalid dates

        startDate.setHours(0, 0, 0, 0);
        holidayDates.add(startDate.toISOString().split('T')[0]);

        // Handle multi-day holidays
        if (holiday.end_date) {
          const endDate = new Date(holiday.end_date);
          if (isNaN(endDate.getTime())) continue; // Skip invalid end dates

          endDate.setHours(0, 0, 0, 0);

          if (endDate > startDate) {
            let currentDate = new Date(startDate);
            currentDate.setDate(currentDate.getDate() + 1);

            while (currentDate <= endDate) {
              holidayDates.add(currentDate.toISOString().split('T')[0]);
              currentDate.setDate(currentDate.getDate() + 1);
            }
          }
        }
      } catch (error) {
        // Skip holidays with invalid date formats
        continue;
      }
    }

    return holidayDates;
  }, [globalHolidays]);

  /**
   * Check if a specific date is a global holiday for a company
   * @param {Date|string} date - The date to check
   * @param {string} companyId - The company ID
   * @returns {boolean}
   */
  const isGlobalHoliday = useCallback((date, companyId) => {
    try {
      const holidayDates = buildHolidayDateSet(companyId);
      const normalizedDate = new Date(date);
      if (isNaN(normalizedDate.getTime())) return false;

      normalizedDate.setHours(0, 0, 0, 0);
      const dateKey = normalizedDate.toISOString().split('T')[0];
      return holidayDates.has(dateKey);
    } catch (error) {
      return false;
    }
  }, [buildHolidayDateSet]);

  /**
   * Memoized holiday date sets by company for efficient lookups
   */
  const holidayDateSets = useMemo(() => {
    const sets = {};
    for (const companyId of Object.keys(globalHolidays)) {
      sets[companyId] = buildHolidayDateSet(companyId);
    }
    return sets;
  }, [globalHolidays, buildHolidayDateSet]);

  // Initialization dependency chain:
  // AuthContext (user exists) → FeatureContext (companies loaded) → DataContext (fetch data)
  // This ensures we have adminCompanyIds and company metadata before querying users/holidays.
  useEffect(() => {
    if (user?.id && !featuresLoading && companies.length > 0) {
      fetchUsers();
      fetchGlobalHolidays();
    } else if (!user?.id) {
      // Reset on logout - memoized `users` and `usersMap` will clear automatically
      setAllUsersData([]);
      setUsersLoading(false);
      setGlobalHolidays({});
      setHolidaysLoading(false);
    }
  }, [user?.id, featuresLoading, companies.length, fetchUsers, fetchGlobalHolidays]);

  const value = {
    // Data
    users,
    usersMap,
    companies, // Pass through from FeatureContext for convenience
    globalHolidays,
    holidayDateSets,

    // Loading states
    loading: usersLoading || featuresLoading || holidaysLoading,
    usersLoading,
    holidaysLoading,

    // Helper functions
    getUserById,
    getUsersByCompany,
    getActiveUsers,
    isGlobalHoliday,
    buildHolidayDateSet,

    // Refresh/invalidate functions
    refreshData,
    refreshUsers: () => fetchUsers(true),
    refreshHolidays: () => fetchGlobalHolidays(true),
    invalidateUsers,
    updateUserOptimistic,

    // Metadata
    lastFetchTime,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}
